import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, ExternalLink, Sparkles } from 'lucide-react';
import type { NavGroup, NavItem } from './navConfig';
import logoDark from '@/assets/logo-dark.png';
import logoDarkSquare from '@/assets/logo-dark-square.png';

type SidebarProps = {
  groups: NavGroup[];
  isOpen?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
};

function isSidebarItemActive(item: NavItem, activePath: string) {
  if (!item.to) {
    return false;
  }

  if (item.to === '/panel/eventos/listado') {
    return activePath.startsWith('/panel/eventos/listado') || /^\/panel\/eventos\/[^/]+$/.test(activePath);
  }

  return activePath.startsWith(item.to);
}

const SidebarItem: React.FC<{ item: NavItem; activePath: string; collapsed?: boolean }> = ({
  item,
  activePath,
  collapsed = false,
}) => {
  const isActive = isSidebarItemActive(item, activePath);
  const Icon = item.icon;
  const collapsedLabelProps = collapsed ? { 'aria-label': item.label, title: item.label } : {};

  if (item.cta && item.to) {
    return (
      <Link
        to={item.to}
        className="mb-3 mt-3 block rounded-2xl px-5 py-3 text-center text-xs font-medium uppercase text-white panel-cta panel-cta--collapsed"
        aria-label={item.label}
        title={collapsed ? item.label : undefined}
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles className="h-4 w-4 opacity-95" />
          <span className="panel-cta__label">{item.label}</span>
        </span>
      </Link>
    );
  }
  return (
    <div>
      {item.to ? (
        <Link
          to={item.to}
          className={`panel-nav-item ${collapsed ? 'panel-nav-item--collapsed' : ''} ${isActive ? 'panel-nav-item--active' : ''}`}
          aria-current={isActive ? 'page' : undefined}
          {...collapsedLabelProps}
        >
          {Icon ? <Icon className="panel-nav-item__icon h-4 w-4" aria-hidden="true" /> : null}
          <span className="panel-nav-item__label">{item.label}</span>
        </Link>
      ) : item.href ? (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`panel-nav-item ${collapsed ? 'panel-nav-item--collapsed' : ''}`}
          {...collapsedLabelProps}
        >
          {Icon ? <Icon className="panel-nav-item__icon h-4 w-4" aria-hidden="true" /> : null}
          <span className="inline-flex items-center panel-nav-item__content">
            <span className="panel-nav-item__label">{item.label}</span>
            {item.external && (
              <span className="panel-nav-item__external" aria-hidden="true">
                <ExternalLink className="panel-nav-item__external-icon h-3.5 w-3.5 text-slate-500" />
              </span>
            )}
          </span>
        </a>
      ) : (
        <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-slate-600">
          <span className="flex items-center gap-2">
            {Icon ? <Icon className="panel-nav-item__icon h-4 w-4" aria-hidden="true" /> : null}
            <span className="panel-nav-item__label">{item.label}</span>
          </span>
          {item.badge && (
            <span className="ml-auto text-xs bg-white/50 px-1.5 py-0.5 rounded">{item.badge}</span>
          )}
        </div>
      )}
      {item.children && (
        <div className={`mt-2 space-y-1 ${collapsed ? 'pl-0 md:pl-0' : 'pl-6'}`}>
          {item.children.map((child) => (
            <SidebarItem key={child.label} item={child} activePath={activePath} collapsed={collapsed} />
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  groups,
  isOpen = false,
  onClose,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const { pathname } = useLocation();
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={
          `fixed inset-y-0 left-0 z-50 w-52 transform transition-transform duration-300 md:sticky md:left-auto md:inset-y-auto md:top-0 md:flex md:h-screen md:self-start md:translate-x-0 md:transition-[width] md:duration-300 shrink-0 flex-col panel-sidebar ${collapsed ? 'panel-sidebar--collapsed md:w-24' : 'md:w-52'} ` +
          `${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`
        }
        aria-label="Sidebar"
      >
        {/* Brand */}
        <div className="panel-sidebar__brand flex items-center px-5 py-5 pb-0">
          <Link to="/panel" aria-label="Ir al Panel" className="panel-sidebar__brand-link inline-flex items-center">
            <img src={logoDark} alt="BloomKeep" className="panel-sidebar__logo panel-sidebar__logo--full h-12 w-auto select-none" />
            <img src={logoDarkSquare} alt="BloomKeep" className="panel-sidebar__logo panel-sidebar__logo--square h-10 w-13 select-none" />
          </Link>
        </div>

        <div className="panel-sidebar__content flex-1 overflow-y-auto px-4 pb-24 pt-2 space-y-6">
          {groups.map((group, index) => (
            <div
              key={group.title || index}
              className={`panel-sidebar__group ${group.title === 'Sistema' ? 'panel-sidebar__group--system' : ''}`}
            >
              {group.title ? (
                <div className="panel-sidebar__group-title mb-3 text-xs uppercase tracking-wider text-slate-400">{group.title}</div>
              ) : null}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <SidebarItem key={item.label} item={item} activePath={pathname} collapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="pointer-events-none absolute bottom-4 right-4 hidden md:block">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="pointer-events-auto panel-sidebar__collapse-button flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/55 text-slate-500 shadow-sm transition hover:bg-white/75 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            aria-label={collapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
            title={collapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
          >
            {collapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-5 w-5" />}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
